import React, { useContext, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withStripes } from '@folio/stripes-core';
import { Callout } from '@folio/stripes/components';
import { useIntl } from 'react-intl';
import WidgetForm from './WidgetForm';
import DashboardsContext from '../../../Context/dashboards/DashboardsContext';
import ReportsContext from '../../../Context/reports/ReportsContext';
import useLocalization from '../../../helpers/useLocalization';
import useWidgetsMutations from '../../../hooks/useWidgetsMutations';
import { WIDGET_QUERY_KEY } from '../../../routes/reportBuilders/WidgetBuilderRoute';

const WIDGET_FORM_STATE_KEY = 'widget_form_state';

function WidgetFormManager(props) {
  const intl = useIntl();
  const { getLocalizedValue } = useLocalization();
  const dashboards = useContext(DashboardsContext);
  const reports = useContext(ReportsContext);
  const { createWidget, updateWidget, getWidget } = useWidgetsMutations();
  const calloutRef = React.useRef();

  const isEditMode = props.location?.pathname?.includes('/edit/');
  const widgetId = props.match?.params?.widgetId || null;
  const pathParts = props.location.pathname.split('/');
  const dashboardIdFromUrl = pathParts[3];

  const currentDashboard = useMemo(() => {
    return dashboards.find(dash => dash.id === dashboardIdFromUrl) || null;
  }, [dashboards, dashboardIdFromUrl]);

  // ── Restore state from sessionStorage (set before navigating to builder) ──
  const getRestoredState = () => {
    try {
      const stored = sessionStorage.getItem(WIDGET_FORM_STATE_KEY);
      if (stored) {
        sessionStorage.removeItem(WIDGET_FORM_STATE_KEY);
        return JSON.parse(stored);
      }
    } catch (e) { /* ignore */ }
    return null;
  };

  const restored = getRestoredState();

  const [initialValues, setInitialValues] = useState(restored?.initialValues || {
    name: [{ locale: 'en', value: '' }, { locale: 'ar', value: '' }],
    desc: [{ locale: 'en', value: '' }, { locale: 'ar', value: '' }],
    displayOrder: 0,
    layoutW: 6,
    isActive: true,
    isVisible: true,
  });

  const [dataSource, setDataSource] = useState(restored?.dataSource || 'reports');
  const [isMultiReport, setIsMultiReport] = useState(restored?.isMultiReport || false);
  const [selectedReportIds, setSelectedReportIds] = useState(restored?.selectedReportIds || []);
  const [selectedReports, setSelectedReports] = useState([]);
  const [pendingQuery, setPendingQuery] = useState(restored?.pendingQuery || null);
  const [widgetDisplayMethod, setWidgetDisplayMethod] = useState(restored?.widgetDisplayMethod || '');
  const [widgetVisibleColumns, setWidgetVisibleColumns] = useState(restored?.widgetVisibleColumns || []);
  const [reportDisplayMethods, setReportDisplayMethods] = useState(restored?.reportDisplayMethods || []);
  const [isLoading, setIsLoading] = useState(false);
  const [restoredReportIds, setRestoredReportIds] = useState([]);

  // Read pending query from sessionStorage (set by WidgetBuilderRoute on "Use this Query")
  useEffect(() => {
    const stored = sessionStorage.getItem(WIDGET_QUERY_KEY);
    if (stored) {
      try {
        setPendingQuery(JSON.parse(stored));
        setDataSource('query');
      } catch (e) { /* ignore */ }
      sessionStorage.removeItem(WIDGET_QUERY_KEY);
    }
  }, []);

  // Fetch widget data in edit mode
  useEffect(() => {
    if (isEditMode && widgetId && !restored) {
      setIsLoading(true);
      getWidget(widgetId)
        .then(widget => {
          setInitialValues({
            name: widget.name || [{ locale: 'en', value: '' }, { locale: 'ar', value: '' }],
            desc: widget.desc || [{ locale: 'en', value: '' }, { locale: 'ar', value: '' }],
            displayOrder: widget.displayOrder || 0,
            layoutW: widget.layout?.w || 6,
            isVisible: widget.isVisible !== false,
            isActive: widget.isActive !== false,
          });
          const src = widget.dataSource || 'reports';
          setDataSource(src);
          const reportIds = widget.widgetReportsConfig?.reportIds || widget.reportsIds || [];
          setIsMultiReport(reportIds.length > 1);
          setRestoredReportIds(reportIds);
          setWidgetDisplayMethod(widget.visualization?.defaultDisplayMethod || '');
          setWidgetVisibleColumns(widget.widgetReportsConfig?.visibleColumns || []);
          if (src === 'query' && widget.queryConfig?.queries?.[0]) {
            setPendingQuery({
              query: widget.queryConfig.queries[0],
              builderType: widget.generalSettings?.builderType || 'cataloging',
              displayMethod: widget.visualization?.defaultDisplayMethod || 'table',
            });
          }
        })
        .catch(err => console.error('Failed to load widget:', err))
        .finally(() => setIsLoading(false));
    }
  }, [isEditMode, widgetId]);

  // Restore selected reports from IDs (edit mode or restored state)
  useEffect(() => {
    const ids = restoredReportIds.length > 0 ? restoredReportIds : selectedReportIds;
    if (ids.length > 0 && reports.length > 0) {
      const found = ids.map(id => reports.find(r => r.id === id)).filter(Boolean);
      setSelectedReports(found);
    }
  }, [restoredReportIds, selectedReportIds, reports]);

  // Save form state to sessionStorage before navigating to builder
  const saveFormStateBeforeNavigation = (formValues) => {
    const state = {
      initialValues: formValues || initialValues,
      dataSource,
      isMultiReport,
      selectedReportIds: selectedReports.map(r => r.id),
      pendingQuery,
      widgetDisplayMethod,
      widgetVisibleColumns,
      reportDisplayMethods,
    };
    sessionStorage.setItem(WIDGET_FORM_STATE_KEY, JSON.stringify(state));
  };

  const buildWidgetPayload = (values) => {
    const reportsIds = selectedReports.map(r => r.id);
    const defaultDisplayMethod = dataSource === 'query'
      ? (pendingQuery?.displayMethod || 'table')
      : (widgetDisplayMethod || selectedReports[0]?.visualization?.defaultDisplayMethod || 'table');

    const reportSettings = selectedReports.map(r => ({
      reportId: r.id,
      displayMethod: r.visualization?.defaultDisplayMethod || 'table',
    }));

    return {
      name: values.name,
      desc: values.desc || [],
      displayOrder: values.displayOrder || 0,
      dashboardId: currentDashboard?.id,
      dataSource,
      widgetType: dataSource === 'query' ? 'custom' : (isMultiReport ? 'multiReports' : 'singleReport'),
      widgetReportsConfig: {
        reportIds: reportsIds,
        reportSettings,
        visibleColumns: widgetVisibleColumns,
      },
      queryConfig: dataSource === 'query' && pendingQuery
        ? { queries: [pendingQuery.query] }
        : undefined,
      isActive: values.isActive !== false,
      isVisible: values.isVisible !== false,
      layout: {
        w: parseInt(values.layoutW, 10) || 6,
        h: 4, x: 0, y: 0,
        isDraggable: true,
        isResizable: true,
      },
      visualization: {
        defaultDisplayMethod,
        displayMethods: reportDisplayMethods.length > 0 ? reportDisplayMethods : undefined,
      },
      reportsIds,
      generalSettings: {
        builderType: pendingQuery?.builderType,
        reportsCount: reportsIds.length,
      },
    };
  };

  const onFormSubmit = async (values) => {
    setIsLoading(true);
    try {
      const payload = buildWidgetPayload(values);
      if (isEditMode && widgetId) {
        await updateWidget.mutateAsync({ id: widgetId, ...payload });
        calloutRef.current?.sendCallout({ type: 'success', message: intl.formatMessage({ id: 'ui-reports.dashboards.widgetForm.callout.updated' }) });
      } else {
        await createWidget.mutateAsync(payload);
        calloutRef.current?.sendCallout({ type: 'success', message: intl.formatMessage({ id: 'ui-reports.dashboards.widgetForm.callout.created' }) });
      }
      props.handleClose();
    } catch (error) {
      calloutRef.current?.sendCallout({ type: 'error', message: intl.formatMessage({ id: 'ui-reports.dashboards.widgetForm.callout.error' }, { error: error.message }) });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <WidgetForm
        handleClose={props.handleClose}
        onSubmit={onFormSubmit}
        initialValues={initialValues}
        editMode={isEditMode}
        currentDashName={getLocalizedValue(currentDashboard?.name) || ''}
        dataSource={dataSource}
        setDataSource={setDataSource}
        isMultiReport={isMultiReport}
        setIsMultiReport={setIsMultiReport}
        selectedReports={selectedReports}
        setSelectedReports={setSelectedReports}
        pendingQuery={pendingQuery}
        setPendingQuery={setPendingQuery}
        widgetDisplayMethod={widgetDisplayMethod}
        setWidgetDisplayMethod={setWidgetDisplayMethod}
        widgetVisibleColumns={widgetVisibleColumns}
        setWidgetVisibleColumns={setWidgetVisibleColumns}
        reportDisplayMethods={reportDisplayMethods}
        setReportDisplayMethods={setReportDisplayMethods}
        onReportSelected={(report) => {
          setInitialValues(prev => ({
            ...prev,
            name: report.name || prev.name,
            desc: report.desc || prev.desc,
          }));
        }}
        onNavigateToBuilder={saveFormStateBeforeNavigation}
        isLoading={isLoading || createWidget.isLoading || updateWidget.isLoading}
      />
      <Callout ref={calloutRef} />
    </>
  );
}

WidgetFormManager.propTypes = {
  handleClose: PropTypes.func.isRequired,
  history: PropTypes.shape({ push: PropTypes.func.isRequired, goBack: PropTypes.func.isRequired }).isRequired,
  location: PropTypes.object,
  match: PropTypes.shape({ params: PropTypes.shape({ widgetId: PropTypes.string }) }),
};

export default withStripes(WidgetFormManager);
